import React, { useCallback, useState, useMemo } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { IpConfigSnapshot, RtuHeader, RtuStaticData } from '../../../types/RtuData';
import { useTranslation } from 'react-i18next';
import { formatDisplayDateTime } from '../../../utils/dateTimeFormat';
import { formatRtuTimeZone } from '../../../utils/rtuTimeZone';

interface InfoRow {
    label: string;
    value: React.ReactNode;
    searchValue?: string;
}

interface RTUInfoProps {
    rtuStaticData: RtuStaticData | null | undefined;
    rtuHeader: RtuHeader | null | undefined;
    /** When true (legacy-v2 static layout), omit Virtual IP / Firewall summary rows that have dedicated cards. */
    stripCompositeRedundancies?: boolean;
}

const RTUInfo = ({ rtuStaticData, rtuHeader, stripCompositeRedundancies = false }: RTUInfoProps) => {
    const { t } = useTranslation();
    const [showSearch, setShowSearch] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const renderVirtualIpDetails = useCallback((virtualIps?: Record<string, IpConfigSnapshot>) => {
        const entries = Object.entries(virtualIps ?? {});
        if (entries.length === 0) {
            return '-';
        }

        return (
            <div style={{ whiteSpace: 'normal' }}>
                <details>
                    <summary style={{ cursor: 'pointer' }}>{t('rtuDetail.infoRows.actions.viewVirtualIps')}</summary>
                    <div style={{ marginTop: '0.35rem', display: 'grid', gap: '0.35rem' }}>
                        {entries.map(([name, config]) => (
                            <div key={name} style={{ padding: '0.35rem', border: '1px solid #e5e7eb', borderRadius: '6px', background: '#fafbfc' }}>
                                <div style={{ fontWeight: 600 }}>{name}</div>
                                <div>{t('rtuDetail.infoRows.actions.configuration')}: {config?.config || '-'}</div>
                                <div>{t('rtuDetail.infoRows.actions.ipAddress')}: {config?.ip || '-'}</div>
                            </div>
                        ))}
                    </div>
                </details>
            </div>
        );
    }, [t]);

    const infoRows: InfoRow[] = useMemo(() => {
        if (!rtuStaticData || !rtuHeader) return [];
        return [
            { label: t('rtuDetail.infoRows.serialNo'), value: rtuHeader.serialNo },
            { label: t('rtuDetail.infoRows.ethernetIp'), value: rtuHeader.ethernetIP },
            { label: t('rtuDetail.infoRows.modemIp'), value: rtuHeader.modemIP },
            { label: t('rtuDetail.infoRows.publicIp'), value: rtuHeader.publicIp },
            { label: t('rtuDetail.infoRows.vpnType'), value: rtuHeader.vpnType },
            {
                label: t('rtuDetail.infoRows.timeZone'),
                value: formatRtuTimeZone({
                    headerTimeZone: rtuHeader.tz,
                    deviceTimeZone: rtuStaticData.timezone,
                    timeZoneCountry: rtuStaticData.tzCountry,
                }),
            },
            { label: t('rtuDetail.infoRows.rtuId'), value: rtuStaticData.wellID },
            { label: t('rtuDetail.infoRows.installDate'), value: formatDisplayDateTime(rtuStaticData.installationDate) },
            { label: t('rtuDetail.infoRows.client'), value: rtuStaticData.client },
            { label: t('rtuDetail.infoRows.name'), value: rtuStaticData.name },
            { label: t('rtuDetail.infoRows.location'), value: rtuStaticData.location },
            { label: t('rtuDetail.infoRows.country'), value: rtuStaticData.country },
            { label: t('rtuDetail.infoRows.latitude'), value: rtuStaticData.latitude },
            { label: t('rtuDetail.infoRows.longitude'), value: rtuStaticData.longitude },
            { label: t('rtuDetail.infoRows.reportDate'), value: formatDisplayDateTime(rtuStaticData.reportDate) },
            { label: t('rtuDetail.infoRows.lastRestart'), value: rtuStaticData.lastRestart },
            { label: t('rtuDetail.infoRows.lastRestartTime'), value: formatDisplayDateTime(rtuStaticData.lastRestartTime) },
            { label: t('rtuDetail.infoRows.dpyVersion'), value: rtuStaticData.dpyVersion },
            { label: t('rtuDetail.infoRows.cfgVersion'), value: rtuStaticData.cfgVersion },
            { label: t('rtuDetail.infoRows.macAddress'), value: rtuStaticData.macId },
            { label: t('rtuDetail.infoRows.imageVersion'), value: rtuStaticData.imageVersion },
            { label: t('rtuDetail.infoRows.appType'), value: rtuStaticData.appType },
            { label: t('rtuDetail.infoRows.deviceType'), value: rtuStaticData.deviceType },
            { label: t('rtuDetail.infoRows.rtuMode'), value: rtuStaticData.rtuMode },
            { label: t('rtuDetail.infoRows.messagingServer'), value: rtuStaticData.messagingServer },
            { label: t('rtuDetail.infoRows.serverTimeout'), value: rtuStaticData.serverConnTimeout },
            { label: t('rtuDetail.infoRows.serialIp'), value: rtuStaticData.serialIp },
            ...(stripCompositeRedundancies
                ? []
                : [
                      { label: t('rtuDetail.infoRows.virtualIpsTotal'), value: rtuStaticData.virtualIp?.total },
                      {
                          label: t('rtuDetail.infoRows.virtualIps'),
                          value: renderVirtualIpDetails(rtuStaticData.virtualIp?.virtualIps),
                          searchValue: JSON.stringify(rtuStaticData.virtualIp?.virtualIps ?? {}),
                      },
                      { label: t('rtuDetail.infoRows.firewallConfig'), value: rtuStaticData.firewall?.config },
                      { label: t('rtuDetail.infoRows.firewallTotal'), value: rtuStaticData.firewall?.total },
                      {
                          label: t('rtuDetail.infoRows.firewallRules'),
                          value: rtuStaticData.firewall?.rulesJson ? (
                              <div style={{ whiteSpace: 'normal' }}>
                                  <details>
                                      <summary style={{ cursor: 'pointer' }}>{t('rtuDetail.infoRows.actions.viewRules')}</summary>
                                      <pre style={{ maxHeight: '140px', overflow: 'auto', margin: '0.25rem 0 0', whiteSpace: 'pre-wrap' }}>
                                          {rtuStaticData.firewall.rulesJson}
                                      </pre>
                                  </details>
                              </div>
                          ) : (
                              '-'
                          ),
                          searchValue: rtuStaticData.firewall?.rulesJson ?? '',
                      },
                  ]),
        ];
    }, [rtuStaticData, rtuHeader, t, stripCompositeRedundancies, renderVirtualIpDetails]);

    const filteredRows = useMemo(() => {
        if (!searchTerm) return infoRows;
        const term = searchTerm.toLowerCase();
        return infoRows.filter(row => 
            row.label.toLowerCase().includes(term) || 
            String(row.searchValue ?? row.value ?? '').toLowerCase().includes(term)
        );
    }, [searchTerm, infoRows]);

    if (!rtuStaticData || !rtuHeader) {
        return null;
    }

    const cardTitle = (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{t('rtuDetail.sections.info')}</span>
            <Button
                icon="pi pi-search"
                className="p-button-text p-button-sm"
                onClick={() => setShowSearch(!showSearch)}
                tooltip={showSearch ? t('search.hide') : t('search.show')}
                tooltipOptions={{ position: 'left' }}
                style={{ padding: '0.15rem 0.3rem', width: 'auto', height: 'auto', color: '#333' }}
            />
        </div>
    );

    const searchBar = showSearch ? (
        <div
            className={stripCompositeRedundancies ? 'rtu-info-card__search-bar' : undefined}
            style={!stripCompositeRedundancies ? { marginBottom: '0.25rem' } : undefined}
        >
            <InputText
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('search.filter')}
                style={{ width: '100%', fontSize: '0.75rem', height: '1.4rem', padding: '0.125rem 0.25rem' }}
            />
        </div>
    ) : null;

    const kvTable = (
        <div className="scrollable-card-content">
            <table className="kv-table">
                <tbody>
                    {filteredRows.map((row, idx) => (
                        <tr key={idx}>
                            <td>{row.label}</td>
                            <td>{row.value ?? '-'}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    return (
        <Card
            title={cardTitle}
            className={`info-card compact-card flat-card${stripCompositeRedundancies ? ' rtu-info-card--legacy-v2-static' : ''}`}
        >
            {stripCompositeRedundancies ? (
                <div className="rtu-info-card__legacy-stack">
                    {searchBar}
                    {kvTable}
                </div>
            ) : (
                <>
                    {searchBar}
                    {kvTable}
                </>
            )}
        </Card>
    );
};

export default RTUInfo;
